import { Segment } from './types';
import { ParamReader } from './ParamReader';

/**
 * Walks a segment tree (recursing into `list` items and `table` cells, SKIPPING `child` subtrees — a child's
 * params belong to its own raw string, never the parent's) and collects every `span` field it
 * finds. Identical spans are deduped; overlapping spans are pruned (the later-starting one is
 * dropped, keeping the earlier-starting span). Result is sorted by start.
 */
export function collectSpans(segments: Segment[]): Array<[number, number]> {
    const collected: Array<[number, number]> = [];

    const walk = (list: Segment[]): void => {
        for (const s of list) {
            if (typeof s === 'string') {
                continue;
            }
            if (s.kind === 'child') {
                continue;
            }
            if (s.kind === 'list') {
                for (const item of s.items) {
                    walk(item);
                }
                continue;
            }
            if (s.kind === 'table') {
                for (const row of s.rows) {
                    for (const cell of row) {
                        walk(cell);
                    }
                }
                continue;
            }
            if (s.kind === 'duration') {
                continue;
            }
            if (s.span) {
                collected.push(s.span);
            }
        }
    };

    walk(segments);

    // Dedupe identical spans.
    const seen = new Set<string>();
    const deduped: Array<[number, number]> = [];
    for (const span of collected) {
        const key = span[0] + ',' + span[1];
        if (!seen.has(key)) {
            seen.add(key);
            deduped.push(span);
        }
    }

    // Sort by start (then end, for stable ordering of same-start spans).
    deduped.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

    // Drop overlaps: keep the earlier-starting span, drop later-starting overlaps.
    const result: Array<[number, number]> = [];
    let lastEnd = -Infinity;
    for (const span of deduped) {
        if (span[0] >= lastEnd) {
            result.push(span);
            lastEnd = span[1];
        }
    }

    return result;
}

/**
 * Walks a segment tree (recursing into `list` items and `table` cells, SKIPPING `child` subtrees — a
 * child card renders its own VFX detail) and collects the `id` of every `kind === 'stateVfx'` segment
 * it finds. Deduped, preserving first-seen order.
 */
export function collectStateVfxIds(segments: Segment[]): number[] {
    const collected: number[] = [];

    const walk = (list: Segment[]): void => {
        for (const s of list) {
            if (typeof s === 'string') {
                continue;
            }
            if (s.kind === 'child') {
                continue;
            }
            if (s.kind === 'list') {
                for (const item of s.items) {
                    walk(item);
                }
                continue;
            }
            if (s.kind === 'table') {
                for (const row of s.rows) {
                    for (const cell of row) {
                        walk(cell);
                    }
                }
                continue;
            }
            if (s.kind === 'stateVfx') {
                collected.push(s.id);
            }
        }
    };

    walk(segments);

    const seen = new Set<number>();
    const deduped: number[] = [];
    for (const id of collected) {
        if (!seen.has(id)) {
            seen.add(id);
            deduped.push(id);
        }
    }

    return deduped;
}

/**
 * Builds `seg.list()` items for a delimited list of ids where every item shares ONE span — the raw
 * region from the first valid id's start to the last valid id's end — instead of each item getting
 * its own. Identical spans dedupe to a single palette slot (see `collectSpans`), so the whole list
 * reads as one color rather than a rainbow of per-item colors.
 *
 * `tokens` are the raw (untrimmed-index-preserving) delim-split tokens of the list, in order.
 * `reader` is a ParamReader scoped over exactly the substring `tokens` came from (so `reader.span(i,
 * delim)` reports each token's absolute position); pass `null` when no absolute offset is available
 * (e.g. a fallback parse path) to get unspanned items instead. `ctor` builds the segment for each
 * valid numeric id (e.g. `seg.skill`, `seg.blow`).
 */
export function sharedSpanListItems(
    tokens: string[],
    reader: ParamReader | null,
    delim: string,
    ctor: (id: number, span?: [number, number]) => Segment,
): Segment[][] {
    let spanStart = -1;
    let spanEnd = -1;
    if (reader) {
        for (let i = 0; i < tokens.length; ++i) {
            if (isNaN(ParamReader.num(tokens[i]))) {
                continue;
            }
            const s = reader.span(i, delim);
            if (!s) {
                continue;
            }
            if (spanStart < 0) {
                spanStart = s[0];
            }
            spanEnd = s[1];
        }
    }
    const listSpan: [number, number] | undefined =
        spanStart >= 0 && spanEnd > spanStart ? [spanStart, spanEnd] : undefined;
    const items: Segment[][] = [];
    tokens.forEach((tok) => {
        const id = ParamReader.num(tok);
        if (isNaN(id)) {
            return;
        }
        items.push([ctor(id, listSpan)]);
    });
    return items;
}

/** Index of `span` within the (pre-sorted) `spans` list, mod 6, or -1 if absent. */
export function spanColorIndex(spans: Array<[number, number]>, span: [number, number]): number {
    const idx = spans.findIndex((s) => s[0] === span[0] && s[1] === span[1]);
    if (idx === -1) {
        return -1;
    }
    return idx % 6;
}

export interface RawParamChunk {
    /** The literal text of this chunk. */
    text: string;
    /** Palette slot (mod 6) for a colored chunk, or -1 for plain text. */
    colorIndex: number;
}

/**
 * Splits `raw` into interleaved plain/colored chunks per `spans` (pre-sorted, non-overlapping
 * [start, end) pairs). Each span's color index is its own position within `spans`, mod 6 —
 * matching `spanColorIndex`. Spans are clamped to the bounds of `raw`; an empty `spans` list
 * yields a single plain-text chunk identical to `raw`.
 */
export function chunkRaw(raw: string, spans: Array<[number, number]>): RawParamChunk[] {
    if (!spans || spans.length === 0) {
        return [{ text: raw, colorIndex: -1 }];
    }

    const len = raw.length;
    const chunks: RawParamChunk[] = [];
    let cursor = 0;

    for (let i = 0; i < spans.length; i++) {
        let start = spans[i][0];
        let end = spans[i][1];

        // Clamp to the bounds of raw.
        if (start < 0) {
            start = 0;
        }
        if (start > len) {
            start = len;
        }
        if (end > len) {
            end = len;
        }
        if (end < start) {
            end = start;
        }

        if (start > cursor) {
            chunks.push({ text: raw.substring(cursor, start), colorIndex: -1 });
            cursor = start;
        }

        if (end > start && start >= cursor) {
            chunks.push({ text: raw.substring(start, end), colorIndex: i % 6 });
            cursor = end;
        }
    }

    if (cursor < len) {
        chunks.push({ text: raw.substring(cursor, len), colorIndex: -1 });
    }

    if (chunks.length === 0) {
        chunks.push({ text: raw, colorIndex: -1 });
    }

    return chunks;
}
